import { useCallback, useEffect, useMemo, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"
import {
  getInitialForm,
  mapBaseHistoryToForm,
  formatDateTimeLabel,
  getTemplatePresentation,
  getStatusPresentation,
  getTemplateCodeLabel,
  getSourceLabel,
  getDateSortValue,
  calculateAgeFromBirthdate,
} from "../components/ClinicalHistoryHelpers"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useClinicalHistoryHub({
  patientId,
  clinicId,
  currentBirthDate,
  setForm,
  setFormBaseline,
}) {
  const [hub, setHub] = useState(null)
  const [hubLoading, setHubLoading] = useState(false)

  const loadHub = useCallback(async () => {
    try {
      if (!patientId || !clinicId) {
        setHub(null)
        const initialForm = getInitialForm()
        setForm(initialForm)
        setFormBaseline(initialForm)
        return
      }

      setHubLoading(true)

      const params = new URLSearchParams({
        patientId,
        clinicId,
      })

      const res = await apiFetch(
        `${API_BASE}/clinical-history/hub?${params.toString()}`
      )
      const data = await res.json().catch(() => null)

      if (!res.ok || !data) {
        setHub(null)
        const initialForm = getInitialForm()
        setForm(initialForm)
        setFormBaseline(initialForm)
        return
      }

      const mappedForm = mapBaseHistoryToForm(data.baseHistory, data.patient)

      setHub(data)
      setForm(mappedForm)
      setFormBaseline(mappedForm)
    } catch {
      setHub(null)
      const initialForm = getInitialForm()
      setForm(initialForm)
      setFormBaseline(initialForm)
    } finally {
      setHubLoading(false)
    }
  }, [patientId, clinicId, setForm, setFormBaseline])

  useEffect(() => {
    if (!patientId || !clinicId) return
    loadHub()
  }, [patientId, clinicId, loadHub])

  const resolvedBirthDate = currentBirthDate || hub?.patient?.birthDate || ""

  const resolvedAge = useMemo(
    () => calculateAgeFromBirthdate(resolvedBirthDate),
    [resolvedBirthDate]
  )

  const isMinorPatient = resolvedAge !== "" && Number(resolvedAge) < 18
  const hasUnknownAge = !resolvedBirthDate

  const hasPediatricHistory = useMemo(() => {
    return (hub?.recentInstances || []).some(
      (item) => String(item?.templateCode || "").toUpperCase() === "PEDIATRIC"
    )
  }, [hub])

  const shouldShowPediatric =
    hasUnknownAge || isMinorPatient || hasPediatricHistory

  const hubFormHistoryItems = useMemo(() => {
    if (!hub) return []

    const items = []

    const templateMap = new Map(
      (hub?.templates || []).map((template) => [
        String(template.code || "").toUpperCase(),
        template,
      ])
    )

    const baseUpdatedAt =
      hub?.baseHistory?.updatedAt ||
      hub?.baseHistory?.createdAt ||
      hub?.summary?.latestBaseHistoryAt ||
      hub?.baseHistory?.date ||
      null

    if (hub?.summary?.baseHistoryExists || hub?.baseHistory) {
      items.push({
        id: "base-history",
        title: "Historia base general",
        subtitle: "Historia principal del paciente",
        summary:
          hub?.baseHistory?.chiefComplaintDescription ||
          hub?.baseHistory?.observations ||
          "Formulario base disponible para seguimiento general.",
        updatedAt: formatDateTimeLabel(baseUpdatedAt),
        sortDate: baseUpdatedAt,
        statusLabel: hub?.summary?.baseHistoryExists ? "Completado" : "Pendiente",
        statusTone: hub?.summary?.baseHistoryExists ? "success" : "neutral",
        sourceLabel: "ClÃ­nica",
        action: "SCROLL_BASE",
        primaryLabel: "Abrir formulario",
        secondaryLabel: "Ir a odontograma",
        secondaryAction: "OPEN_ODONTOGRAM",
      })
    }

    ;(hub?.recentInstances || []).forEach((item) => {
      const templateCode = String(item.templateCode || "").toUpperCase()
      const templateMeta = templateMap.get(templateCode)
      const presentation = getTemplatePresentation({
        code: templateCode,
        name: item.templateName,
      })
      const status = getStatusPresentation(item.status)

      let action = "OPEN_UNAVAILABLE"
      let primaryLabel = "PrÃ³ximamente"

      if (templateMeta?.isLocked) {
        action = "OPEN_UPGRADE"
        primaryLabel = "Mejorar plan"
      } else if (templateCode === "INITIAL_DIAGNOSIS") {
        action = "OPEN_DIAGNOSIS"
        primaryLabel = "Abrir"
      } else if (templateCode === "ENDO") {
        action = "OPEN_ENDO"
        primaryLabel = "Abrir"
      } else if (templateCode === "PERIO") {
        action = "OPEN_PERIO"
        primaryLabel = "Abrir"
      } else if (templateCode === "SURGERY") {
        action = "OPEN_SURGERY"
        primaryLabel = "Abrir"
      } else if (templateCode === "ORTHO") {
        action = "OPEN_ORTHO"
        primaryLabel = "Abrir"
      } else if (templateCode === "PEDIATRIC") {
        action = "OPEN_PEDIATRIC"
        primaryLabel = "Abrir"
      } else if (templateCode === "QR_PREINTAKE") {
        action = "OPEN_QR_REVIEW"
        primaryLabel = "Ver captura"
      }

      items.push({
        id: `instance-${item.id}`,
        title:
          item.title ||
          item.templateName ||
          getTemplateCodeLabel(templateCode),
        subtitle: getTemplateCodeLabel(templateCode),
        summary:
          item.summary ||
          presentation.description ||
          "Sin resumen clÃ­nico capturado.",
        updatedAt: formatDateTimeLabel(
          item.updatedAt || item.submittedAt || item.createdAt
        ),
        sortDate: item.updatedAt || item.submittedAt || item.createdAt || null,
        statusLabel: templateMeta?.isLocked ? "Bloqueado" : status.label,
        statusTone: templateMeta?.isLocked ? "warning" : status.tone,
        sourceLabel: getSourceLabel(item.source),
        action,
        primaryLabel,
        secondaryLabel:
          templateCode === "QR_PREINTAKE" && !templateMeta?.isLocked
            ? "Usar datos"
            : "",
        secondaryAction:
          templateCode === "QR_PREINTAKE" && !templateMeta?.isLocked
            ? "USE_QR_DATA"
            : "",
      })
    })

    return items.sort(
      (a, b) => getDateSortValue(b.sortDate) - getDateSortValue(a.sortDate)
    )
  }, [hub])

  const hubTimelineItems = useMemo(() => {
    if (!hub) return []

    const items = []

    const templateMap = new Map(
      (hub?.templates || []).map((template) => [
        String(template.code || "").toUpperCase(),
        template,
      ])
    )

    const baseUpdatedAt =
      hub?.baseHistory?.updatedAt ||
      hub?.baseHistory?.createdAt ||
      hub?.summary?.latestBaseHistoryAt ||
      hub?.baseHistory?.date ||
      null

    if (hub?.summary?.baseHistoryExists || hub?.baseHistory) {
      items.push({
        id: "timeline-base",
        templateCode: "BASE_GENERAL",
        title: "Historia base actualizada",
        description:
          hub?.baseHistory?.chiefComplaintDescription ||
          "Se actualizÃ³ la historia clÃ­nica general del paciente.",
        timestamp: formatDateTimeLabel(baseUpdatedAt),
        sortDate: baseUpdatedAt,
        statusLabel: "Completado",
        statusTone: "success",
        actionLabel: "Abrir formulario",
        action: "SCROLL_BASE",
      })
    }

    ;(hub?.recentInstances || []).forEach((item) => {
      const templateCode = String(item.templateCode || "").toUpperCase()
      const templateMeta = templateMap.get(templateCode)
      const status = getStatusPresentation(item.status)

      let action = "OPEN_UNAVAILABLE"
      let actionLabel = ""

      if (templateMeta?.isLocked) {
        action = "OPEN_UPGRADE"
        actionLabel = "Mejorar plan"
      } else if (templateCode === "INITIAL_DIAGNOSIS") {
        action = "OPEN_DIAGNOSIS"
        actionLabel = "Abrir"
      } else if (templateCode === "ENDO") {
        action = "OPEN_ENDO"
        actionLabel = "Abrir"
      } else if (templateCode === "PERIO") {
        action = "OPEN_PERIO"
        actionLabel = "Abrir"
      } else if (templateCode === "SURGERY") {
        action = "OPEN_SURGERY"
        actionLabel = "Abrir"
      } else if (templateCode === "ORTHO") {
        action = "OPEN_ORTHO"
        actionLabel = "Abrir"
      } else if (templateCode === "PEDIATRIC") {
        action = "OPEN_PEDIATRIC"
        actionLabel = "Abrir"
      } else if (templateCode === "QR_PREINTAKE") {
        action = "OPEN_QR_REVIEW"
        actionLabel = "Ver captura"
      }

      items.push({
        id: `timeline-instance-${item.id}`,
        templateCode,
        title: item.title || `${item.templateName || getTemplateCodeLabel(templateCode)}`,
        description:
          item.summary ||
          `${item.templateName || getTemplateCodeLabel(templateCode)} registrada desde ${getSourceLabel(item.source)}.`,
        timestamp: formatDateTimeLabel(
          item.updatedAt || item.submittedAt || item.createdAt
        ),
        sortDate: item.updatedAt || item.submittedAt || item.createdAt || null,
        statusLabel: templateMeta?.isLocked ? "Bloqueado" : status.label,
        statusTone: templateMeta?.isLocked ? "warning" : status.tone,
        actionLabel,
        action,
      })
    })

    return items.sort(
      (a, b) => getDateSortValue(b.sortDate) - getDateSortValue(a.sortDate)
    )
  }, [hub])

  const hubSpecialtyLauncherItems = useMemo(() => {
    if (!hub?.templates?.length) return []

    const recentByCode = new Map(
      (hub?.recentInstances || []).map((item) => [
        String(item.templateCode || "").toUpperCase(),
        item,
      ])
    )

    const preferredOrder = [
      "INITIAL_DIAGNOSIS",
      "ENDO",
      "PERIO",
      "SURGERY",
      "ORTHO",
      "PEDIATRIC",
    ]

    return preferredOrder
      .filter((code) => {
        if (code !== "PEDIATRIC") return true
        return shouldShowPediatric
      })
      .map((code) => {
        const template = (hub.templates || []).find(
          (item) => String(item.code || "").toUpperCase() === code
        )

        if (!template) return null

        const recent = recentByCode.get(code)
        const isLocked = Boolean(template.isLocked)

        let action = "OPEN_UNAVAILABLE"
        let actionLabel = "PrÃ³ximamente"

        if (isLocked) {
          action = "OPEN_UPGRADE"
          actionLabel = "Mejorar plan"
        } else if (code === "INITIAL_DIAGNOSIS") {
          action = "OPEN_DIAGNOSIS"
          actionLabel = "Abrir"
        } else if (code === "ENDO") {
          action = "OPEN_ENDO"
          actionLabel = "Abrir"
        } else if (code === "PERIO") {
          action = "OPEN_PERIO"
          actionLabel = "Abrir"
        } else if (code === "SURGERY") {
          action = "OPEN_SURGERY"
          actionLabel = "Abrir"
        } else if (code === "ORTHO") {
          action = "OPEN_ORTHO"
          actionLabel = "Abrir"
        } else if (code === "PEDIATRIC") {
          action = "OPEN_PEDIATRIC"
          actionLabel = "Abrir"
        }

        const pediatricTitle =
          code === "PEDIATRIC" && !isMinorPatient && hasPediatricHistory
            ? "Historial pediÃ¡trico previo"
            : getTemplateCodeLabel(code)

        const pediatricDescription =
          code === "PEDIATRIC" && !isMinorPatient && hasPediatricHistory
            ? "Registro pediÃ¡trico previo del paciente, conservado para consulta o continuidad histÃ³rica."
            : getTemplatePresentation(template).description

        return {
          id: `launcher-${code}`,
          title: pediatricTitle,
          description: pediatricDescription,
          updatedAt: recent
            ? formatDateTimeLabel(recent.updatedAt || recent.submittedAt || recent.createdAt)
            : "Sin registro",
          statusLabel: isLocked
            ? "Bloqueado"
            : recent
              ? "Disponible"
              : "Nuevo",
          statusTone: isLocked
            ? "warning"
            : recent
              ? "success"
              : "neutral",
          action,
          actionLabel,
        }
      })
      .filter(Boolean)
  }, [hub, shouldShowPediatric, isMinorPatient, hasPediatricHistory])

  const hubFormHistoryCount = hubFormHistoryItems.length
  const hubTimelineCount = hubTimelineItems.length
  const hubSpecialtyCount = hubSpecialtyLauncherItems.length
  const clinicalHubCount =
    hubFormHistoryCount + hubTimelineCount + hubSpecialtyCount

  const baseFormSectionCount = 10

  return {
    hub,
    hubLoading,
    reloadHub: loadHub,
    hubFormHistoryItems,
    hubTimelineItems,
    hubSpecialtyLauncherItems,
    hubFormHistoryCount,
    hubTimelineCount,
    hubSpecialtyCount,
    clinicalHubCount,
    baseFormSectionCount,
  }
}
